/**
 * Telemetria do cliente: o que só o navegador enxerga, indo para o servidor.
 *
 * ## Por que existe
 *
 * Vários defeitos desta fase só apareciam no console do Márcio, e a única
 * forma de eu os enxergar era pedir que ele copiasse e colasse — a escuta que
 * caiu para o navegador, o autoplay bloqueado, a transição recusada. Cada
 * diagnóstico custava uma ida e volta de minutos, e alguns custaram horas.
 *
 * Isto não substitui os olhos dele: ele continua sendo quem ouve a voz e vê a
 * cara do domo. Substitui a parte mecânica — o que o próprio código já sabe e
 * estava contando só para si mesmo.
 *
 * ## O que ele NÃO é
 *
 * Não é um canal de log. O servidor valida cada `evento` contra um catálogo
 * fechado e descarta o resto, e aqui o mesmo catálogo aparece como tipo. Texto
 * livre autenticado indo para disco é como um endpoint de diagnóstico vira
 * problema de operação.
 *
 * ## Como ele se comporta quando dá errado
 *
 * Some. Telemetria que quebra a interface é pior que telemetria nenhuma: erro
 * de rede é engolido, lote perdido não é retentado, e a fila tem teto. O
 * usuário nunca deve saber que isto existe.
 */

import { authFetch } from '../auth';
import { U1_API_PATH } from './config';

/** O mesmo catálogo que o servidor aceita. Divergir daqui é perder o evento. */
export type EventoTelemetria =
  | 'voz.realtime.aberto'
  | 'voz.realtime.caiu'
  | 'voz.navegador.aberto'
  | 'voz.microfone.negado'
  | 'voz.bargein'
  | 'voz.ttfa'
  | 'voz.tts.falhou'
  | 'voz.autoplay.bloqueado'
  // Qual voz de fato saiu. É o monitoramento que responde "o Will está usando
  // mais de uma voz?" com um número em vez de com o ouvido de quem escuta.
  | 'voz.falou'
  | 'estado.transicao_invalida';

interface Registro {
  evento: EventoTelemetria;
  ms?: number;
  detalhe?: string;
}

const URL_TELEMETRIA = `${U1_API_PATH}/telemetry`;
/** Junta o que acontecer nesta janela num lote só. */
const AGRUPAR_MS = 4_000;
/** Teto da fila. O servidor corta em 32; cortar aqui evita mandar o que ele vai jogar fora. */
const MAX_FILA = 32;

let fila: Registro[] = [];
let timer: number | null = null;
let sessionId: string | null = null;
let ligado = true;

/** Amarra os relatos à sessão. Sem isto o servidor sabe o quê, mas não de onde. */
export function telemetriaDaSessao(id: string | null): void {
  sessionId = id;
}

/** Desliga de vez — para teste, ou se algum dia isto virar preferência. */
export function desligarTelemetria(): void {
  ligado = false;
  fila = [];
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
}

export function relatar(evento: EventoTelemetria, dados: Omit<Registro, 'evento'> = {}): void {
  if (!ligado) return;

  // O console continua recebendo tudo: quem estiver com ele aberto não deve
  // perder informação por ela também ir para o servidor.
  const sufixo = dados.ms === undefined ? '' : ` (${Math.round(dados.ms)} ms)`;
  console.info(`[u1] ${evento}${sufixo}${dados.detalhe ? ` — ${dados.detalhe}` : ''}`);

  if (fila.length >= MAX_FILA) {
    // Descarta o mais antigo: num surto, o que acabou de acontecer explica
    // melhor o estado atual que o começo do surto.
    fila.shift();
  }
  fila.push({ evento, ...dados });

  if (timer === null) {
    timer = window.setTimeout(() => {
      timer = null;
      void enviar();
    }, AGRUPAR_MS);
  }
}

async function enviar(): Promise<void> {
  if (fila.length === 0) return;
  const lote = fila;
  fila = [];
  try {
    await authFetch(URL_TELEMETRIA, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ eventos: lote, ...(sessionId === null ? {} : { session_id: sessionId }) }),
      // `keepalive` deixa o lote sair mesmo se a aba estiver fechando — que é
      // exatamente quando o diagnóstico interessa mais.
      keepalive: true,
    });
  } catch {
    // Lote perdido é lote perdido. Retentar telemetria durante uma queda de
    // rede acrescenta requisição justamente quando a rede está ruim, e o que
    // se ganha é um diagnóstico atrasado sobre um estado que já passou.
  }
}

/**
 * Manda o que estiver na fila agora.
 *
 * Vale a pena na saída da página: sem isto, tudo o que aconteceu nos últimos
 * segundos — que costuma ser o motivo de a pessoa ter fechado a aba — morre
 * junto com ela.
 */
export function escoarTelemetria(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  void enviar();
}

if (typeof window !== 'undefined') {
  // `pagehide` e não `unload`: `unload` não dispara em navegador móvel nem com
  // bfcache, que é onde a aba mais some sem aviso.
  window.addEventListener('pagehide', escoarTelemetria);
}
